import { z } from "zod";

/**
 * Spec 033 US4 — per-user paid-spend view DTOs.
 *
 * Shapes match `specs/033-admin-observability/contracts/user-api.md`.
 * Returned by `GET /api/me/usage/paid-spend` + `GET /api/me/usage/recent-activity`.
 *
 * Authenticated; no admin guard required.
 */

const paidSpendProviderSchema = z.object({
  spendCentsToday: z.number().int(),
  spendCentsThisMonth: z.number().int(),
  capCentsPerDay: z.number().int(),
  capCentsPerMonth: z.number().int(),
  remainingCentsToday: z.number().int(),
  remainingCentsThisMonth: z.number().int(),
  capStatus: z.enum(["ok", "warning", "blocked"]),
  resetTodayAt: z.string(),
  resetMonthAt: z.string(),
});

export const paidSpendResponseSchema = z.object({
  ai: paidSpendProviderSchema,
  proxy: paidSpendProviderSchema,
});

export type PaidSpendResponse = z.infer<typeof paidSpendResponseSchema>;
export type PaidSpendProvider = z.infer<typeof paidSpendProviderSchema>;

// Recent-activity reuses the admin drill-down shape (the user owns this
// data; nothing administratively sensitive). Mirroring it keeps the client
// list component reusable across the user + admin views.
//
// Per-row cost is in MILLI-cents (1/1000 of a cent) for the same reason as the
// admin payload: a single call costs 13-75 milli-cents, so a cent-valued field
// here is 0 on every row. See the note on `providerAggregateSchema`.
export const recentActivityResponseSchema = z.object({
  groqRequests: z.array(
    z.object({
      id: z.string(),
      operation: z.string().nullable(),
      model: z.string(),
      promptTokens: z.number().int().nullable(),
      completionTokens: z.number().int().nullable(),
      costMilliCents: z.number().int().nullable(),
      status: z.string(),
      createdAt: z.string(),
    }),
  ),
  proxyRequests: z.array(
    z.object({
      id: z.string(),
      board: z.string(),
      provider: z.string(),
      requestCount: z.number().int(),
      bytes: z.number().int().nullable(),
      costMilliCents: z.number().int().nullable(),
      status: z.string(),
      createdAt: z.string(),
    }),
  ),
  nextCursor: z.string().nullable(),
});

export type RecentActivityResponse = z.infer<typeof recentActivityResponseSchema>;
